'use client';

import React, { useState } from 'react';
import { toast } from 'sonner';
import Toggle from '@/components/ui/Toggle';

interface NotifSetting {
  id: string;
  label: string;
  description: string;
  email: boolean;
  inApp: boolean;
}

const initialSettings: NotifSetting[] = [
  { id: 'notif-send-complete', label: 'Campaign send complete', description: 'Notify when a campaign finishes sending', email: true, inApp: true },
  { id: 'notif-send-fail', label: 'Campaign send failure', description: 'Alert if a scheduled send fails to deliver', email: true, inApp: true },
  { id: 'notif-high-bounce', label: 'High bounce rate alert', description: 'Warn when bounce rate exceeds 2% on a send', email: true, inApp: false },
  { id: 'notif-spam-complaint', label: 'Spam complaint threshold', description: 'Alert when spam complaints exceed 0.08%', email: true, inApp: true },
  { id: 'notif-unsub-spike', label: 'Unsubscribe spike', description: 'Notify when unsubscribes are 3x the daily average', email: false, inApp: true },
  { id: 'notif-list-import', label: 'List import complete', description: 'Confirm when a subscriber import finishes', email: true, inApp: false },
  { id: 'notif-domain-verify', label: 'Domain verification', description: 'Notify when a sender domain is verified or fails', email: true, inApp: true },
  { id: 'notif-weekly-digest', label: 'Weekly performance digest', description: 'Weekly summary of your top campaign metrics', email: true, inApp: false },
];

export default function NotificationsSettings() {
  const [settings, setSettings] = useState<NotifSetting[]>(initialSettings);

  const toggle = (id: string, field: 'email' | 'inApp') => {
    setSettings((prev) =>
      prev.map((s) => s.id === id ? { ...s, [field]: !s[field] } : s)
    );
  };

  const handleSave = () => {
    // Backend integration point: PATCH /api/notifications/preferences
    toast.success('Notification preferences saved');
  };

  return (
    <div className="space-y-6">
      <div className="card overflow-hidden">
        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
          <div>
            <h2 className="section-header">Notification Preferences</h2>
            <p className="text-sm text-muted-foreground mt-0.5">Choose how and when you want to be notified</p>
          </div>
          <div className="flex items-center gap-8 text-xs font-semibold text-muted-foreground">
            <span>Email</span>
            <span>In-App</span>
          </div>
        </div>
        <div className="divide-y divide-border">
          {settings.map((s) => (
            <div key={s.id} className="flex items-center px-6 py-4 hover:bg-muted/20 transition-colors">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-foreground">{s.label}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{s.description}</p>
              </div>
              <div className="flex items-center gap-8 flex-shrink-0 ml-4">
                <Toggle checked={s.email} onChange={() => toggle(s.id, 'email')} />
                <Toggle checked={s.inApp} onChange={() => toggle(s.id, 'inApp')} />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end">
        <button onClick={handleSave} className="btn-primary">Save Preferences</button>
      </div>
    </div>
  );
}